import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from 'react-bootstrap';
import ReactApexChart from "react-apexcharts";
import ApexCharts from 'apexcharts';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { getLocalStorageValue, isNullUndefined, isNullUndefinedOrEmpty, setLocalStorageValue } from '../../../../../../common/Helpers';
import Loading from '../../../../../Templates/Site/Loading';
import { useSetInicialValues } from '../../../../../../hooks/component/sKPI/useOfConsolidatedGroupConsolidatedChart';
import { ActionButton, DirectionalHint, Toggle, TooltipHost } from '@fluentui/react';
import './style.css';
import { __onGoToBoardKpiOperational } from '../../../../../../common/SystemRedirections';
import { action_setCompareContracts_ChartMaximized } from '../../../../../../reducers/actions/KPI/compareContractsActions';

const CustomApexLegend = ({
	chartId,
	series,
	colors,
	showLegend,
	resetKey
}) => {
	const [hiddenSeries, setHiddenSeries] = useState({});

	useEffect(() => {
		setHiddenSeries({});
	}, [resetKey]);

	if (!showLegend || !series || series.length === 0) {
		return null;
	}

	const handleToggleSerie = (event, serieName) => {
		event.stopPropagation();

		ApexCharts.exec(chartId, 'toggleSeries', serieName);

		setHiddenSeries(prev => ({
			...prev,
			[serieName]: !prev[serieName]
		}));
	};

	return (
		<div className="custom-apex-legend">
			{
				series.map((serie, index) => {
					const serieName = serie.name;
					const isHidden = hiddenSeries[serieName] === true;
					const color = colors[index] ?? '#999999';

					return (
						<div
							key={`${serieName}-${index}`}
							className={`custom-apex-legend-item ${isHidden ? 'is-hidden' : ''}`}
							onClick={(event) => handleToggleSerie(event, serieName)}
						>
							<span
								className="custom-apex-legend-marker"
								style={{
									backgroundColor: color
								}}
							/>

							<TooltipHost
								content={serieName}
								directionalHint={DirectionalHint.leftCenter}
							>
								<span className="custom-apex-legend-text">
									{serieName}
								</span>
							</TooltipHost>
						</div>
					);
				})
			}
		</div>
	);
};

function CONSOLIDATE_GROUP_CONSOLIDATE_CHART(props) {
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const {
		inputParam_UseFake = false,
		inputParam_HideHeader = false,
		inputParam_ComponentContent = null,
		inputParam_OnMaximize = null,
		inputParam_IsMaximized = 'normal'
	} = props;

	const {
		_chartData: chartData,
		_fireLoading: fireLoading,
		_contratosBase: contratosBase,
		_chartMaximized: chartMaximized,
	} = useSelector((state) => state.compareContractsScope);

	const [chartSerieClickEnabled, setChartSerieClickEnabled] = useState(isNullUndefinedOrEmpty(getLocalStorageValue('PARAM_COOKIE_TRANSVERSAL_PORTFOLIO_CHART_OPTIONS_SERIECLICK_ENABLED', null, false)) ? true : getLocalStorageValue('PARAM_COOKIE_TRANSVERSAL_PORTFOLIO_CHART_OPTIONS_SERIECLICK_ENABLED', null, false));
	const [showLegend, setShowLegend] = useState(isNullUndefinedOrEmpty(getLocalStorageValue('PARAM_COOKIE_TRANSVERSAL_PORTFOLIO_CHART_OPTIONS_SHOWLEGEND', null, false)) ? true : getLocalStorageValue('PARAM_COOKIE_TRANSVERSAL_PORTFOLIO_CHART_OPTIONS_SHOWLEGEND', null, false));
	const [chartConfigOption, setChartConfigOption] = useState(isNullUndefinedOrEmpty(getLocalStorageValue('PARAM_COOKIE_TRANSVERSAL_PORTFOLIO_CHART_OPTIONS_PINEDSCALE', null, false)) ? 1 : getLocalStorageValue('PARAM_COOKIE_TRANSVERSAL_PORTFOLIO_CHART_OPTIONS_PINEDSCALE', null, false));
	const [chartConfig, setChartConfig] = useState(null);

	useSetInicialValues(chartConfig, setChartConfig);

	const focusedDataLabelSerieRef = useRef(null);

	const contractSeriesLength = !isNullUndefined(chartData) && !isNullUndefined(chartData.contractSeries)
		? chartData.contractSeries.length
		: 0;

	const chartColors = useMemo(() => {
		return generateDistinctColors(contractSeriesLength);
	}, [contractSeriesLength]);

	const legendResetKey = useMemo(() => {
		if (isNullUndefined(chartData) || isNullUndefined(chartData.contractSeries)) {
			return `${chartConfigOption}-empty`;
		}

		return `${chartConfigOption}-${chartData.contractSeries.map(x => x.name).join('|')}`;
	}, [chartConfigOption, chartData]);

	const getDashArray = () => {
		const aux_chartData = chartData;

		return !isNullUndefined(aux_chartData)
			? aux_chartData.contractSeries.map((serie) => (serie.isDashed ? 5 : 0))
			: [];
	};

	function generateDistinctColors(amount) {
		const colors = [];
		const goldenRatioConjugate = 0.618033988749895;
		let hue = Math.random();

		for (let i = 0; i < amount; i++) {
			hue += goldenRatioConjugate;
			hue %= 1;

			colors.push(hslToHex(hue * 360, 60, 50));
		}

		return colors;
	}

	function hslToHex(h, s, l) {
		l /= 100;

		const a = s * Math.min(l, 1 - l) / 100;

		const f = n => {
			const k = (n + h / 30) % 12;
			const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);

			return Math.round(255 * color).toString(16).padStart(2, '0');
		};

		return `#${f(0)}${f(8)}${f(4)}`.toUpperCase();
	}

	const getDataLabelsOptions = (enabled, enabledOnSeries = []) => ({
		enabled,
		enabledOnSeries,
		formatter: (value) => (!isNullUndefined(value) ? `${value}%` : undefined),
		style: {
			colors: ["#000"],
		},
		offsetY: -10
	});

	const getChartEvents = () => ({
		mouseMove: function (event, chartContext, config) {
			const baseEl = chartContext?.w?.globals?.dom?.baseEl;

			if (!baseEl) {
				return;
			}

			const r = baseEl.getBoundingClientRect();
			const x = event?.clientX ?? (r.left + r.width / 2);
			const isLeftHalf = x < (r.left + r.width / 2);

			baseEl.classList.toggle("apx-tt-right", isLeftHalf);
			baseEl.classList.toggle("apx-tt-left", !isLeftHalf);

			const seriesIndex = config?.seriesIndex;

			if (
				seriesIndex !== undefined &&
				seriesIndex !== null &&
				seriesIndex >= 0 &&
				focusedDataLabelSerieRef.current !== seriesIndex
			) {
				focusedDataLabelSerieRef.current = seriesIndex;

				chartContext.updateOptions({
					dataLabels: getDataLabelsOptions(true, [seriesIndex])
				}, false, false);
			}
		},

		mouseLeave: function (event, chartContext) {
			const baseEl = chartContext?.w?.globals?.dom?.baseEl;

			if (!baseEl) {
				return;
			}

			baseEl.classList.remove("apx-tt-right", "apx-tt-left");

			focusedDataLabelSerieRef.current = null;

			chartContext.updateOptions({
				dataLabels: getDataLabelsOptions(false, [])
			}, false, false);
		},

		dataPointSelection: (event, chartContext, config) => {
			if (chartSerieClickEnabled === true) {
				const globalConfig = chartContext.w.config;
				const serieName = globalConfig.series[config.seriesIndex].name;

				const selectedContract = contratosBase.find(x => `${x.contractCode} - ${x.supplierName}` === serieName);

				if (!isNullUndefined(selectedContract)) {
					__onGoToBoardKpiOperational(selectedContract.elementId, 4);
				}
			}
		},

		markerClick: (event, chartContext, { seriesIndex, dataPointIndex, config }) => {
			if (chartSerieClickEnabled === true) {
				const globalConfig = chartContext.w.config;
				const serieName = globalConfig.series[seriesIndex].name;

				const selectedContract = contratosBase.find(x => `${x.contractCode} - ${x.supplierName}` === serieName);

				if (!isNullUndefined(selectedContract)) {
					__onGoToBoardKpiOperational(selectedContract.elementId, 4);
				}
			}
		}
	});

	const getChartOptionsStatic = () => {
		const aux_chartData = chartData;

		const chartOptionStatic = {
			chart: {
				id: 'CHART_PORTFOLIO',
				type: "line",
				toolbar: {
					show: true,
				},
				events: getChartEvents(),
			},
			annotations: chartConfig ? {
				yaxis: [{
					y: chartConfig.maxKPI,
					borderColor: '#a0402a',
					height: "10px",
					strokeDashArray: 6,
					label: {
						borderColor: '#a0402a',
						style: {
							color: '#fff',
							background: '#a0402a',
						},
						text: `(${chartConfig.maxKPI})`,
					}
				}]
			} : undefined,
			stroke: {
				dashArray: getDashArray(),
			},
			colors: chartColors,
			labels: !isNullUndefined(aux_chartData) ? aux_chartData.contractCategories : [],
			xaxis: {
				title: {
					text: t("data-lang-lbl-datemonthyear"),
				},
			},
			yaxis: {
				labels: {
					formatter: (value) => `${value}%`,
				},
				title: {
					text: t("data-lang-lbl-percent")
				},
				min: chartConfig ? chartConfig.logroMinimo : undefined,
				max: chartConfig ? chartConfig.logroMaximo : undefined
			},
			dataLabels: getDataLabelsOptions(false, []),
			tooltip: {
				intersect: false,
				shared: false,
				y: {
					formatter: (value) => (!isNullUndefined(value) ? `${value}%` : t('no-data')),
				},
			},
			markers: {
				size: 6,
			},
			legend: {
				show: false
			},
		};

		return chartOptionStatic;
	};

	const getChartOptionsDynamic = () => {
		const aux_chartData = chartData;

		const chartOptionDynamic = {
			chart: {
				id: 'CHART_PORTFOLIO',
				type: "line",
				toolbar: {
					show: true,
				},
				events: getChartEvents(),
			},
			stroke: {
				dashArray: getDashArray(),
			},
			colors: chartColors,
			labels: !isNullUndefined(aux_chartData) ? aux_chartData.contractCategories : [],
			xaxis: {
				title: {
					text: t("data-lang-lbl-datemonthyear"),
				},
			},
			yaxis: {
				labels: {
					formatter: (value) => `${value}%`,
				},
				title: {
					text: t("data-lang-lbl-percent")
				},
				minY: aux_chartData ? ((a) => a.length ? Math.min(...a) : 0)(aux_chartData.contractSeries.flatMap(s => s.data).map(Number).filter(Number.isFinite)) : undefined,
				maxY: aux_chartData ? ((a) => a.length ? Math.max(...a) : 0)(aux_chartData.contractSeries.flatMap(s => s.data).map(Number).filter(Number.isFinite)) : undefined,
				forceNiceScale: false
			},
			dataLabels: {
				enabled: true,
				formatter: (value) => (!isNullUndefined(value) ? `${value}%` : undefined),
				style: {
					colors: ["#000"],
				},
				offsetY: -10
			},
			tooltip: {
				intersect: false,
				shared: false,
				y: {
					formatter: (value) => (!isNullUndefined(value) ? `${value}%` : t('no-data')),
				},
			},
			markers: {
				size: 6,
			},
			legend: {
				show: false
			},
		};

		return chartOptionDynamic;
	};

	const onChangeChartConfigOption = () => {
		setChartConfigOption(chartConfigOption === 1 ? 2 : 1);
		setLocalStorageValue('PARAM_COOKIE_TRANSVERSAL_PORTFOLIO_CHART_OPTIONS_PINEDSCALE', chartConfigOption === 1 ? 2 : 1);
	};

	const onChangeChartClickSerieClick = () => {
		setChartSerieClickEnabled(!chartSerieClickEnabled);
		setLocalStorageValue('PARAM_COOKIE_TRANSVERSAL_PORTFOLIO_CHART_OPTIONS_SERIECLICK_ENABLED', !chartSerieClickEnabled);
	};

	const onChangeChartMaximizeClick = () => {
		inputParam_OnMaximize && inputParam_OnMaximize ();
		dispatch(action_setCompareContracts_ChartMaximized(!chartMaximized));
	};

	const onManageShowLegend = () => {
		setShowLegend(!showLegend);
		setLocalStorageValue('PARAM_COOKIE_TRANSVERSAL_PORTFOLIO_CHART_OPTIONS_SHOWLEGEND', !showLegend);
	};

	const renderChartWithCustomLegend = (options, series) => {
		return (
			<div
				className="portfolio-chart-with-custom-legend"
				style={{
					display: "flex",
					width: "100%",
					height: "600px",
					minWidth: 0,
					minHeight: 0
				}}
			>
				<div
					style={{
						flex: "1 1 auto",
						minWidth: 0,
						minHeight: 0
					}}
				>
					<ReactApexChart
						options={options}
						series={series}
						type="line"
						height="100%"
						width="100%"
					/>
				</div>

				<CustomApexLegend
					chartId="CHART_PORTFOLIO"
					series={series}
					colors={chartColors}
					showLegend={showLegend}
					resetKey={legendResetKey}
				/>
			</div>
		);
	};

	const renderContent = () => {
		const aux_chartData = chartData;

		return (
			<>
				{
					!fireLoading && chartConfigOption === 1 &&
					<>
						{
							!isNullUndefined(chartConfig) &&
							!isNullUndefined(aux_chartData) &&
							!isNullUndefined(aux_chartData.contractSeries) &&
							!isNullUndefined(aux_chartData.contractCategories) &&
							renderChartWithCustomLegend(
								getChartOptionsStatic(),
								aux_chartData.contractSeries
							)
						}

						{
							isNullUndefined(aux_chartData) &&
							<span>{t('data-lang-msg-selectcontractandpillartocontinue')}</span>
						}
					</>
				}

				{
					!fireLoading && chartConfigOption !== 1 &&
					<>
						{
							!isNullUndefined(chartConfig) &&
							!isNullUndefined(aux_chartData) &&
							!isNullUndefined(aux_chartData.contractSeries) &&
							!isNullUndefined(aux_chartData.contractCategories) &&
							renderChartWithCustomLegend(
								getChartOptionsDynamic(),
								aux_chartData.contractSeries
							)
						}

						{
							isNullUndefined(aux_chartData) &&
							<span>{t('data-lang-msg-selectcontractandpillartocontinue')}</span>
						}
					</>
				}

				{
					fireLoading &&
					<Loading
						inputParam_Style={{ marginTop: 0 }}
						inputParam_Width='30px'
						inputParam_Height='30px'
					/>
				}
			</>
		);
	};

	return (
		<>
			{
				!inputParam_UseFake &&
				<Card className='h-100'>
					{
						!inputParam_HideHeader &&
						<Card.Header className="card-header-compact">
							<Card.Title className="d-flex justify-content-between">
								<b>{(!inputParam_UseFake && inputParam_ComponentContent !== null) ? (inputParam_ComponentContent.boardComponentTitle ?? t('data-lang-boxname-group_consolidated_chart')) : t('data-lang-boxname-group_consolidated_chart')}</b>

								<div className='toolbar d-flex'>									
									<Toggle
										label={chartSerieClickEnabled === true ? t('data-lang-lbl-serieclickactivated') : t('data-lang-lbl-serieclickunactivated')}
										checked={chartSerieClickEnabled === true}
										onChange={() => onChangeChartClickSerieClick()}
										className="my-auto"
										styles={{ container: { paddingTop: "4px", paddingRight: "16px" }, root: { float: "right", display: "flex" }, label: { marginRight: "5px" } }}
									/>

									<Toggle
										label={chartMaximized === true ? t('data-lang-lbl-chartmazimized') : t('data-lang-lbl-chartunmaximized')}
										checked={chartMaximized === true}
										onChange={() => onChangeChartMaximizeClick()}
										className="my-auto"
										styles={{ container: { paddingTop: "4px", paddingRight: "16px" }, root: { float: "right", display: "flex" }, label: { marginRight: "5px" } }}
									/>

									<Toggle
										label={chartConfigOption === 1 ? t('data-lang-lbl-static') : t('data-lang-lbl-dynamic')}
										checked={chartConfigOption === 1}
										onChange={() => onChangeChartConfigOption()}
										className="my-auto"
										styles={{ container: { paddingTop: "4px", paddingRight: "16px" }, root: { float: "right", display: "flex" }, label: { marginRight: "5px" } }}
									/>

									<Toggle
										label={showLegend === true ? t('data-lang-lbl-showlegend') : t('data-lang-lbl-hidelegend')}
										checked={showLegend === true}
										onChange={() => onManageShowLegend()}
										className="my-auto"
										styles={{ container: { paddingTop: "4px", paddingRight: "8px" }, root: { float: "right", display: "flex" }, label: { marginRight: "5px" } }}
									/>
								</div>
							</Card.Title>
						</Card.Header>
					}

					<Card.Body className={`h-100 p-0 m-0${isNullUndefined(chartData) ? ' text-center d-flex justify-content-center align-items-center' : ''}`}>
						{renderContent()}
					</Card.Body>
				</Card>
			}

			{inputParam_UseFake && renderContent()}
		</>
	);
}

CONSOLIDATE_GROUP_CONSOLIDATE_CHART.propTypes = {
	inputParam_Height: PropTypes.number,
	inputParam_ReportMode: PropTypes.bool,
};

export default CONSOLIDATE_GROUP_CONSOLIDATE_CHART;